import { BaseMapper, clampPercentage } from '@ps/utils';
import {
  QuotaCriteria,
  QuotaCriterionDto,
  rangeBoundsFrom,
  SurveyQuota,
  SurveyQuotaDto,
  SurveyQuotaSupplier,
  QuotaSupplierDto,
} from '@ps/survey-utils';

export class QuotaMapper extends BaseMapper<SurveyQuotaDto, SurveyQuota> {
  private readonly _supplierMapper = new QuotaSupplierMapper();

  public fromDto(dto: SurveyQuotaDto): SurveyQuota {
    const _criterionMapper = new QuotaCriteriaMapper();
    return {
      id: dto.buyer_quota_id,
      psQuotaId: dto.ps_quota_id,
      label: this._label(dto),
      hasCustomTitle: !!dto.quota_title,
      groupKey: dto.group_key,
      locked: dto.locked,
      fielded: dto.achieved,
      goal: dto.current_target,
      currentlyOpen: dto.currently_open,
      maximum: dto.maximum,
      allocationPercentage: clampPercentage(dto.percentage),
      hasInField: !!dto.in_field?.total_complete,
      isUnknown: false,
      inProgress: dto.bip ?? 0,
      requiredCount: dto.required_count,
      validClicks: dto.buyer_valid_clks,
      suppliers: this._supplierMapper.fromDto(dto.suppliers ?? []),
      criteria: _criterionMapper.fromDto(dto.criteria),
    };
  }

  private _label(dto: SurveyQuotaDto): string {
    if (dto.quota_title) {
      return dto.quota_title;
    }
    const parts = dto.criteria
      .flatMap((criterion) => this._criterionLabels(criterion))
      .filter((part) => part.length > 0);
    return parts.length > 0 ? parts.join(', ') : dto.group_key;
  }

  /**
   * A criterion is labelled by its named conditions (e.g. "Male") or, for
   * range-based qualifications like Age, by its ranges (e.g. "18 - 24").
   */
  private _criterionLabels(criterion: QuotaCriterionDto): string[] {
    const conditions = (criterion.condition_names ?? []).filter(
      (name) => name.length > 0,
    );
    if (conditions.length > 0) {
      return conditions;
    }
    return (criterion.range_sets ?? [])
      .filter(
        (range) =>
          typeof range.from === 'number' && typeof range.to === 'number',
      )
      .map((range) => `${range.from} - ${range.to}`);
  }
}

export class QuotaSupplierMapper extends BaseMapper<
  QuotaSupplierDto[],
  SurveyQuotaSupplier[]
> {
  public fromDto(dto: QuotaSupplierDto[]): SurveyQuotaSupplier[] {
    return dto.map((supplier) => ({
      supplierRefId: supplier.supplier_ref_id,
      name: supplier.name,
      currentTarget: supplier.current_target,
      currentlyOpen: supplier.currently_open,
      supplierCurrentlyOpen: supplier.sup_currently_open,
      fielded: supplier.complete_fielded,
      completeNeeded: supplier.complete_needed,
      validClicks: supplier.buyer_valid_clks,
      inProgress: supplier.bip,
      lastStartDate: supplier.last_start_date,
      lastCompleteDate: supplier.last_complete_date,
      isPdsApplied: supplier.is_pds_applied,
      fee: supplier.fee,
    }));
  }
}

export class QuotaCriteriaMapper extends BaseMapper<
  QuotaCriterionDto[],
  QuotaCriteria[]
> {
  // Every range consumer reads the scalar rangeMin/rangeMax, so mapping range_sets alone left a
  // reloaded range quota looking like an uncommitted blank row. Spreads to {} for multi-select.
  public fromDto(dto: QuotaCriterionDto[]): QuotaCriteria[] {
    return dto.map((criterion) => ({
      qualificationCode: criterion.qualification_code,
      qualificationName: criterion.qualification_name,
      conditionCodes: criterion.condition_codes,
      conditionNames: criterion.condition_names,
      rangeSets: criterion.range_sets,
      childAgeUnit: criterion.child_age_unit,
      ...rangeBoundsFrom(criterion.range_sets),
      originalMaximum: criterion.original_maximum,
      originalMinimum: criterion.original_minimum,
      originalPercentage: criterion.original_percentage,
      originalRequiredCount: criterion.original_required_count,
      originalNumber: criterion.original_number,
      originalFlex: criterion.original_flex,
    }));
  }
}
